import type { InferenceDistributionEntry } from '@/types/components/audiences/inferred-mix-editor'

/**
 * Coerces a distribution to non-negative **integer** percentages that sum to **100**.
 * The identity-field PATCH DTO requires `Number.isInteger(percentage)` and sum === 100.
 */
export function normalizeInferenceDistributionToApi(
  entries: { value: string; percentage: number }[],
): { value: string; percentage: number }[] {
  if (entries.length === 0) {
    return []
  }
  const out = entries.map((e) => ({
    value: e.value,
    percentage: Math.max(0, Math.min(100, Math.round(Number(e.percentage)))),
  }))
  let s = out.reduce((a, b) => a + b.percentage, 0)
  if (s === 100) {
    return out
  }
  const diff = 100 - s
  for (let i = 0; i < out.length; i += 1) {
    const next = out[i].percentage + diff
    if (next >= 0 && next <= 100) {
      out[i] = { ...out[i], percentage: next }
      return out
    }
  }
  if (diff > 0) {
    let d = diff
    let guard = 0
    while (d > 0 && guard < 10_000) {
      for (let i = 0; i < out.length; i += 1) {
        if (d <= 0) break
        if (out[i].percentage < 100) {
          out[i] = { ...out[i], percentage: out[i].percentage + 1 }
          d -= 1
        }
      }
      guard += 1
    }
  } else {
    let d = -diff
    let guard = 0
    while (d > 0 && guard < 10_000) {
      for (let i = 0; i < out.length; i += 1) {
        if (d <= 0) break
        if (out[i].percentage > 0) {
          out[i] = { ...out[i], percentage: out[i].percentage - 1 }
          d -= 1
        }
      }
      guard += 1
    }
  }
  return out
}

/**
 * Full distribution for `accept_inferred` when the user deviates from the suggestion.
 * Never send a diff-only list — the API validates the array alone sums to 100.
 */
export function buildAcceptInferredCustomValuesPayload(
  draftDistribution: InferenceDistributionEntry[] | null,
  suggestedValues: { value: string; percentage: number }[] | undefined,
): { customValues?: { value: string; percentage: number }[] } {
  const suggestedMap = new Map(
    (suggestedValues ?? []).map((v) => [v.value, v.percentage]),
  )
  const raw = (draftDistribution ?? []).map((e) => ({
    value: e.value,
    percentage: e.percentage,
  }))
  if (raw.length === 0) {
    return {}
  }
  const matchesSuggested = raw.every(
    (e) => suggestedMap.get(e.value) === e.percentage,
  )
  if (matchesSuggested) {
    return {}
  }
  return {
    customValues: normalizeInferenceDistributionToApi(raw),
  }
}
